import type React from "react"
import { Link } from "react-router-dom"
import Navbar from "../../components/Navbar"
import Footer from "../../components/Footer"
import Image from "../../components/Image"
import { CheckCircle, Plane, Shield, Clock, AlertTriangle, Award, Zap, Snowflake } from "lucide-react"

const AirportOperationsPage: React.FC = () => {
  const airportApplications = [
    {
      title: "Runway & Taxiway Maintenance",
      description: "Comprehensive maintenance solutions for runways, taxiways, and aprons",
      image: "https://placehold.co/600x400?text=Runway+Maintenance",
      attachments: ["Runway Sweeper", "Surface Cleaner", "Rubber Removal", "Crack Sealer"],
      benefits: ["Extends runway surface life", "Maintains proper friction levels", "Reduces FOD hazards"],
    },
    {
      title: "Snow & Ice Management",
      description: "Rapid snow removal and de-icing operations to maintain airport operations in winter conditions",
      image: "https://placehold.co/600x400?text=Snow+Management",
      attachments: ["Snow Plough", "Snow Blower", "De-icing Sprayer", "Runway Brush"],
      benefits: ["Minimizes operational delays", "Ensures safe takeoff/landing conditions", "Rapid deployment"],
    },
    {
      title: "Glycol Recovery",
      description: "Specialized glycol recovery and management for environmental compliance",
      image: "https://placehold.co/600x400?text=Glycol+Recovery",
      attachments: ["Glycol Recovery Unit", "Liquid Collection System", "High-Capacity Tank", "Precision Applicator"],
      benefits: ["Environmental compliance", "Cost recovery of glycol", "Reduced environmental impact"],
    },
    {
      title: "FOD Detection & Removal",
      description: "Foreign Object Debris detection and removal to maintain runway safety",
      image: "https://placehold.co/600x400?text=FOD+Removal",
      attachments: ["FOD Sweeper", "Magnetic Bar", "Vacuum System", "Debris Collector"],
      benefits: ["Prevents aircraft damage", "Enhances safety", "Reduces operational risks"],
    },
    {
      title: "Airfield Lighting Maintenance",
      description: "Precision maintenance of runway and taxiway lighting systems",
      image: "https://placehold.co/600x400?text=Lighting+Maintenance",
      attachments: ["Runway Light Cleaner", "Precision Brush", "Light Maintenance Kit", "Cable Trencher"],
      benefits: ["Ensures proper visibility", "Extends light fixture life", "Maintains regulatory compliance"],
    },
    {
      title: "Ground Support Equipment",
      description: "Versatile ground support operations for aircraft servicing areas",
      image: "https://placehold.co/600x400?text=Ground+Support",
      attachments: ["High Tip Bucket", "Pallet Fork", "Tow Bar", "Equipment Carrier"],
      benefits: ["Versatile support capabilities", "Reduces equipment needs", "Improves operational efficiency"],
    },
  ]

  const airportAttachments = [
    {
      name: "Runway Sweeper",
      description: "High-performance sweeping for runway surfaces",
      specs: "3m working width, high-speed operation",
    },
    {
      name: "Glycol Recovery Unit",
      description: "Specialized system for glycol collection and recovery",
      specs: "1000L capacity, 98% recovery efficiency",
    },
    {
      name: "Runway Light Cleaner",
      description: "Precision cleaning of runway lighting fixtures",
      specs: "Adjustable pressure, specialized brushes",
    },
    {
      name: "FOD Collector",
      description: "Advanced debris collection system for FOD management",
      specs: "Magnetic + vacuum system, 99.8% collection rate",
    },
    {
      name: "Runway Snow Plough",
      description: "High-speed snow removal for runways and taxiways",
      specs: "3.5m width, hydraulic angle adjustment",
    },
    {
      name: "De-icing Sprayer",
      description: "Precision application of de-icing fluids",
      specs: "2000L capacity, computer-controlled application",
    },
    {
      name: "Surface Friction Tester",
      description: "Measures runway surface friction for safety compliance",
      specs: "Real-time data logging, regulatory compliance",
    },
    {
      name: "Rubber Removal System",
      description: "Specialized system for runway rubber deposit removal",
      specs: "High-pressure water + chemical application",
    },
  ]

  const airportBenefits = [
    {
      icon: Clock,
      title: "Minimized Downtime",
      description: "Rapid deployment and efficient operation to minimize runway closure times",
    },
    {
      icon: Shield,
      title: "Enhanced Safety",
      description: "Comprehensive maintenance to ensure safe aircraft operations",
    },
    {
      icon: Award,
      title: "Regulatory Compliance",
      description: "Meets all ICAO and local aviation authority requirements",
    },
    {
      icon: Zap,
      title: "Operational Efficiency",
      description: "One machine performs multiple critical airport maintenance tasks",
    },
  ]

  const airportCaseStudies = [
    {
      airport: "Melbourne International Airport",
      challenge: "Needed to reduce runway maintenance downtime while improving surface quality",
      solution: "Multihog MX with specialized runway maintenance attachments",
      results: [
        "Reduced maintenance closure times by 35%",
        "Improved runway friction measurements by 22%",
        "Decreased FOD incidents by 45%",
        "Achieved full regulatory compliance",
      ],
      quote:
        "The Multihog system has transformed our runway maintenance operations, allowing us to maintain the highest safety standards while minimizing operational disruptions.",
      spokesperson: "John Davies, Head of Airfield Operations",
    },
    {
      airport: "Brisbane Regional Airport",
      challenge: "Winter operations causing significant delays and safety concerns",
      solution: "Multihog CX with winter operations package",
      results: [
        "Reduced winter-related delays by 60%",
        "Improved glycol recovery by 85%",
        "Decreased de-icing fluid usage by 30%",
        "Enhanced operational reliability in all weather conditions",
      ],
      quote:
        "The versatility of the Multihog system has allowed us to maintain consistent operations throughout winter conditions that previously would have caused significant disruptions.",
      spokesperson: "Sarah Johnson, Airport Operations Manager",
    },
  ]

  const safetyFeatures = [
    {
      title: "FOD Prevention",
      description: "Advanced systems to prevent foreign object debris on runways",
      icon: AlertTriangle,
    },
    {
      title: "Surface Friction Management",
      description: "Maintains proper runway friction for safe aircraft operations",
      icon: Shield,
    },
    {
      title: "All-Weather Operation",
      description: "Reliable performance in all weather conditions",
      icon: Snowflake,
    },
    {
      title: "Visibility Enhancement",
      description: "High-visibility features for safe operation in all conditions",
      icon: Plane,
    },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6 inline-block bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full text-sm font-medium">
              Airport Operations
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Specialized
              <br />
              <span className="text-blue-400">Airport Solutions</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Comprehensive maintenance solutions for runways, taxiways, and airport ground support operations. Maximize
              operational uptime while maintaining the highest safety standards.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-medium rounded-lg transition-colors inline-block"
              >
                Get Airport Solutions Quote
              </Link>
              <Link
                to="/models"
                className="border border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 text-lg font-medium rounded-lg transition-colors inline-block"
              >
                View Airport-Ready Models
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Airport Stats */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Trusted by Airports Worldwide</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="text-3xl font-bold text-blue-600 mb-2">50+</div>
                <div className="text-gray-600">Airports Served</div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="text-3xl font-bold text-blue-600 mb-2">35%</div>
                <div className="text-gray-600">Reduced Downtime</div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="text-3xl font-bold text-blue-600 mb-2">24/7</div>
                <div className="text-gray-600">Operational Support</div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="text-3xl font-bold text-blue-600 mb-2">100%</div>
                <div className="text-gray-600">Regulatory Compliance</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Airport Applications Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Airport Applications</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Specialized solutions for the unique challenges of airport operations, designed to maximize safety,
                efficiency, and regulatory compliance.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {airportApplications.map((app, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                >
                  <Image
                    src={app.image || "/placeholder.svg"}
                    alt={app.title}
                    className="w-full h-48 object-cover"
                    width={600}
                    height={400}
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{app.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{app.description}</p>

                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Key Benefits:</h4>
                      <ul className="space-y-1">
                        {app.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <CheckCircle className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Recommended Attachments:</h4>
                      <div className="flex flex-wrap gap-2">
                        {app.attachments.map((attachment, idx) => (
                          <span
                            key={idx}
                            className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs"
                          >
                            {attachment}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Safety & Compliance Section */}
      <section className="py-20 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Airport Safety & Compliance</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Our airport solutions are designed to meet and exceed all international aviation safety standards and
                regulatory requirements.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {safetyFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center">
                  <div className="bg-blue-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <feature.icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>

            <div className="mt-16 bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Regulatory Compliance</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-blue-600 font-bold text-lg mb-2">ICAO</div>
                  <p className="text-gray-600 text-sm">
                    Compliant with International Civil Aviation Organization standards and recommended practices
                  </p>
                </div>
                <div className="text-center">
                  <div className="text-blue-600 font-bold text-lg mb-2">CASA</div>
                  <p className="text-gray-600 text-sm">
                    Meets Civil Aviation Safety Authority requirements for Australian airports
                  </p>
                </div>
                <div className="text-center">
                  <div className="text-blue-600 font-bold text-lg mb-2">ISO 9001</div>
                  <p className="text-gray-600 text-sm">
                    Quality management systems certified to international standards
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Airports Choose Multihog</h2>
              <p className="text-lg text-gray-600">
                Experience the advantages that make Multihog the preferred choice for airport operations
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {airportBenefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="bg-blue-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <benefit.icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Attachments Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Specialized Airport Attachments</h2>
              <p className="text-lg text-gray-600">
                Purpose-built attachments designed specifically for airport operations
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {airportAttachments.map((attachment, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center mb-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">{attachment.name}</h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-2">{attachment.description}</p>
                  <p className="text-blue-600 text-xs font-medium">{attachment.specs}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Airport Success Stories</h2>
              <p className="text-lg text-gray-600">
                Real results from airports that have transformed their operations with Multihog
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {airportCaseStudies.map((study, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                  <div className="flex items-center mb-6">
                    <Plane className="h-8 w-8 text-blue-600 mr-3" />
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{study.airport}</h3>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Challenge:</h4>
                    <p className="text-gray-600 mb-4">{study.challenge}</p>

                    <h4 className="font-semibold text-gray-900 mb-2">Solution:</h4>
                    <p className="text-blue-600 font-medium mb-4">{study.solution}</p>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Results:</h4>
                    <ul className="space-y-2">
                      {study.results.map((result, idx) => (
                        <li key={idx} className="flex items-center text-gray-600">
                          <CheckCircle className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
                          {result}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 italic text-gray-700">
                    <p className="mb-2">"{study.quote}"</p>
                    <p className="text-sm text-blue-600 font-medium">— {study.spokesperson}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section className="py-20 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Airport ROI Calculator</h2>
              <p className="text-lg text-gray-600">
                See how quickly your Multihog investment can pay for itself through operational efficiencies
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Potential Annual Savings</h3>
                  <ul className="space-y-4">
                    <li className="flex justify-between items-center pb-2 border-b border-gray-100">
                      <span className="text-gray-700">Reduced Equipment Fleet</span>
                      <span className="font-semibold text-blue-600">$120,000</span>
                    </li>
                    <li className="flex justify-between items-center pb-2 border-b border-gray-100">
                      <span className="text-gray-700">Maintenance Cost Reduction</span>
                      <span className="font-semibold text-blue-600">$45,000</span>
                    </li>
                    <li className="flex justify-between items-center pb-2 border-b border-gray-100">
                      <span className="text-gray-700">Labor Efficiency</span>
                      <span className="font-semibold text-blue-600">$85,000</span>
                    </li>
                    <li className="flex justify-between items-center pb-2 border-b border-gray-100">
                      <span className="text-gray-700">Reduced Downtime Value</span>
                      <span className="font-semibold text-blue-600">$150,000</span>
                    </li>
                    <li className="flex justify-between items-center font-bold text-lg">
                      <span>Total Annual Savings</span>
                      <span className="text-blue-600">$400,000</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Typical ROI Timeline</h3>
                  <div className="bg-blue-50 p-4 rounded-lg mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700">Average Investment</span>
                      <span className="font-semibold">$350,000</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700">Annual Savings</span>
                      <span className="font-semibold">$400,000</span>
                    </div>
                    <div className="flex justify-between items-center font-bold">
                      <span>Payback Period</span>
                      <span className="text-blue-600">10.5 months</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    * Actual savings may vary based on specific airport operations, size, and current equipment fleet.
                    Contact us for a personalized ROI analysis.
                  </p>
                </div>
              </div>
              <div className="mt-8 text-center">
                <Link
                  to="/contact"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-block"
                >
                  Get Custom ROI Analysis
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Transform Your Airport Operations</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join the growing number of airports worldwide that trust Multihog for their critical maintenance
              operations. Improve safety, efficiency, and regulatory compliance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-medium rounded-lg transition-colors inline-block"
              >
                Schedule Airport Consultation
              </Link>
              <Link
                to="/models"
                className="border border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 text-lg font-medium rounded-lg transition-colors inline-block"
              >
                View Airport-Ready Models
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default AirportOperationsPage
 